import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";

export class CustomValidators {
  // Validador para CURP
  static curp(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const curp = String(control.value).toUpperCase().trim();

    // 1) Formato
    const curpPattern =
      /^[A-Z][AEIOU][A-Z]{2}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[HM](AS|BC|BS|CC|CL|CM|CS|CH|DF|DG|GT|GR|HG|JC|MC|MN|MS|NT|NL|OC|PL|QT|QR|SL|SP|SR|TC|TS|TL|VZ|YN|ZS|NE)[B-DF-HJ-NP-TV-Z]{3}[0-9A-Z]\d$/;

    if (!curpPattern.test(curp)) return { invalidCurp: true };

    // 2) Fecha real (siglo a partir del 17º carácter)
    const yy = parseInt(curp.slice(4, 6), 10);
    const mm = parseInt(curp.slice(6, 8), 10);
    const dd = parseInt(curp.slice(8, 10), 10);
    const sigloChar = curp[16];
    const yearBase = /[0-9]/.test(sigloChar) ? 1900 : 2000; // 0-9 -> 1900s, A-J -> 2000s
    const y = yearBase + yy;

    const isValidDate = (Y: number, M: number, D: number) => {
      const dt = new Date(Y, M - 1, D);
      return (
        dt.getFullYear() === Y && dt.getMonth() === M - 1 && dt.getDate() === D
      );
    };
    if (!isValidDate(y, mm, dd)) return { invalidCurpDate: true };

    // 3) Dígito verificador (18º): pesos 18..2, diccionario 0-9 A..Z (incluye Ñ)
    const DICT = "0123456789ABCDEFGHIJKLMNÑOPQRSTUVWXYZ";
    let sum = 0;
    for (let i = 0; i < 17; i++) {
      const val = DICT.indexOf(curp[i]);
      if (val === -1) return { invalidCurp: true };
      const weight = 18 - i; // i=0->18, ..., i=16->2
      sum += val * weight;
    }
    const dv = (10 - (sum % 10)) % 10;
    const last = Number(curp[17]);
    if (Number.isNaN(last) || last !== dv) return { invalidCurpChecksum: true };

    return null;
  }

  // Validador para RFC
  static rfc(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const rfcPattern =
      /^[A-ZÑ&]{3,4}[0-9]{2}(0[1-9]|1[0-2])(0[1-9]|1[0-9]|2[0-9]|3[0-1])$/;

    return rfcPattern.test(control.value.toUpperCase())
      ? null
      : { invalidRfc: true };
  }

  // Validador para homoclave
  static homoclave(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const homoclavePattern = /^[A-Z0-9]{3}$/;

    return homoclavePattern.test(control.value.toUpperCase())
      ? null
      : { invalidHomoclave: true };
  }

  // Validador para fecha de nacimiento
  static birthDate(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const datePattern =
      /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/([0-9]{4})$/;

    if (!datePattern.test(control.value)) {
      return { invalidDateFormat: true };
    }

    const [day, month, year] = control.value.split("/").map(Number);
    const date = new Date(year, month - 1, day);
    const today = new Date();

    // Verificar que la fecha sea válida
    if (
      date.getDate() !== day ||
      date.getMonth() !== month - 1 ||
      date.getFullYear() !== year
    ) {
      return { invalidDate: true };
    }

    // Verificar que sea mayor de 18 años
    const age = today.getFullYear() - date.getFullYear();
    const monthDiff = today.getMonth() - date.getMonth();

    if (
      age < 18 ||
      (age === 18 && monthDiff < 0) ||
      (age === 18 && monthDiff === 0 && today.getDate() < date.getDate())
    ) {
      return { underAge: true };
    }

    // Verificar que no sea una fecha futura
    if (date > today) {
      return { futureDate: true };
    }

    return null;
  }

  // Validador para código postal mexicano
  static postalCode(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const postalCodePattern = /^[0-9]{5}$/;

    return postalCodePattern.test(control.value)
      ? null
      : { invalidPostalCode: true };
  }

  // Validador para teléfono mexicano
  static mexicanPhone(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const phonePattern = /^[0-9]{10}$/;

    return phonePattern.test(control.value.replace(/\s/g, ""))
      ? null
      : { invalidPhone: true };
  }

  // Validador para email
  static email(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    return emailPattern.test(control.value) ? null : { invalidEmail: true };
  }

  // Validador para monto mínimo
  static minAmount(minValue: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const value = parseFloat(control.value.replace(/,/g, ''));
    
      return value >= minValue
        ? null
        : { minAmount: { requiredAmount: minValue, actualAmount: value } };
    };
  }
}
